import { useEffect, useState } from 'react';

const SECTIONS = [
  {
    title: 'Voucher Entry',
    shortcuts: [
      ['Enter / Tab',     'Next field'],
      ['Shift+Tab',       'Previous field'],
      ['F2',              'Jump to date field'],
      ['F10',             'Change voucher type'],
      ['Alt+F6',          'Switch to Credit Note'],
      ['Alt+F5',          'Switch to Debit Note'],
      ['Alt+R',           'Repeat last voucher (as template)'],
      ['Ctrl+A',          'Accept / Save voucher'],
      ['Escape',          'Cancel / Go back'],
      ['Ins / +',         'Add new row in grid'],
      ['Del / -',         'Delete current row'],
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      ['Alt+F1',          'Company features (Gateway)'],
      ['Ctrl+K / /',      'Quick access search'],
      ['?',               'Keyboard shortcuts (this panel)'],
      ['↑ / ↓',           'Move between menu items'],
      ['Enter',           'Select item / Drill down'],
      ['Backspace / Esc', 'Go back / Cancel'],
      ['F12',             'Change company'],
    ],
  },
  {
    title: 'Voucher Types (Gateway shortcut)',
    shortcuts: [
      ['F', 'Receipt'],
      ['P', 'Payment'],
      ['C', 'Contra'],
      ['J', 'Journal'],
      ['S', 'Sales'],
      ['V', 'Purchase'],
      ['N', 'Credit Note'],
      ['T', 'Debit Note'],
    ],
  },
  {
    title: 'Print & Export',
    shortcuts: [
      ['Ctrl+P',  'Print current report / voucher'],
      ['Alt+E',   'Export to Excel'],
      ['Alt+D',   'Export to PDF'],
      ['W',       'Share via WhatsApp (in print preview)'],
    ],
  },
  {
    title: 'Date Navigation',
    shortcuts: [
      ['D-M-YYYY', 'Type date directly (e.g. 1-4-2024)'],
      ['T',         "Today's date (in date field)"],
      ['+ / -',     'Next / Previous day'],
      ['PgUp/PgDn', 'Next / Previous month'],
    ],
  },
];

export const KeyboardHelpOverlay = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === '?' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setVisible(v => !v);
        return;
      }
      if (e.key === 'Escape' && visible) { e.preventDefault(); setVisible(false); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) setVisible(false); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[820px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-[#1e8585] text-white flex items-center justify-between px-4 py-2.5 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-bold">⌨ Keyboard Shortcuts</span>
            <span className="text-[11px] text-teal-200 ml-2">Press ? to toggle  •  Esc to close</span>
          </div>
          <button onClick={() => setVisible(false)} className="text-white/70 hover:text-white text-xl leading-none px-1">✕</button>
        </div>

        {/* Grid of sections */}
        <div className="overflow-y-auto p-4 grid grid-cols-2 gap-4">
          {SECTIONS.map(section => (
            <div key={section.title} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#f0fafa] px-3 py-1.5 text-[11px] font-bold text-[#1e8585] uppercase tracking-wide border-b border-gray-200">
                {section.title}
              </div>
              <table className="w-full">
                <tbody>
                  {section.shortcuts.map(([key, desc]) => (
                    <tr key={key} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-1.5 w-[140px]">
                        <span className="font-mono text-[11px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded border border-gray-300">
                          {key}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-[12px] text-gray-700">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-400 shrink-0">
          Tip: Most screens support keyboard-first navigation. Mouse is optional. Press ? from any non-input area to see this panel.
        </div>
      </div>
    </div>
  );
};
